import type { AuditContext, AuditEvent, AuditMaintenanceContext } from "./auditTypes";

const HIDDEN_AUDIT_FIELDS = new Set(["tenant", "tenant_id"]);

export function shouldDisplayAuditField(key: string): boolean {
  return !HIDDEN_AUDIT_FIELDS.has(key.toLowerCase());
}

export function humaniseKey(key: string): string {
  let normalized = key;
  if (normalized.toLowerCase() === "id") {
    return "Record";
  }
  if (/_id$/i.test(normalized)) {
    normalized = normalized.replace(/_id$/i, "");
  }
  return normalized.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatAuditDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const RECORD_LABEL_KEYS = [
  "name",
  "name_en",
  "recipe_name",
  "plu",
  "po_number",
  "shipping_number",
  "invoice_number",
  "lot_number",
  "equipment_name",
] as const;

export function getAuditRecordLabel(event: AuditEvent): string | null {
  const metadata = event?.metadata;
  if (!metadata) return null;

  if (event?.entity?.label) return String(event.entity.label);

  const snapshot = metadata.snapshot;
  if (snapshot) {
    for (const key of RECORD_LABEL_KEYS) {
      if (snapshot[key]) return String(snapshot[key]);
    }
  }

  const changes = metadata.changes ?? {};
  for (const key of RECORD_LABEL_KEYS) {
    if (changes[key]?.to) return String(changes[key].to);
    if (changes[key]?.from) return String(changes[key].from);
  }

  return null;
}

type RecipeLineItem = {
  bom_name?: string;
  name?: string;
  bom_id?: number | string;
  id?: number | string;
  quantity?: number | string;
  recipe_per_kg?: number | string;
};

export function formatRecipeLineItem(item: unknown): string {
  if (!item || typeof item !== "object") return String(item ?? "");
  const row = item as RecipeLineItem;
  const name =
    row.bom_name || row.name || `#${row.bom_id ?? row.id ?? "?"}`;
  const parts = [name];
  if (row.quantity != null && row.quantity !== "")
    parts.push(`qty ${row.quantity}`);
  if (row.recipe_per_kg != null && row.recipe_per_kg !== "")
    parts.push(`${row.recipe_per_kg}/kg`);
  return parts.join(" · ");
}

type RelationItem = {
  name?: string;
  label?: string;
  bom_name?: string;
  id?: number | string;
};

export function formatAuditRelationValue(
  relationKey: string,
  val: unknown,
): string | string[] | null {
  if (val === null || val === undefined || val === "") return null;
  if (!Array.isArray(val)) return String(val);
  if (val.length === 0) return null;

  const first = val[0];
  if (first && typeof first === "object") {
    if (relationKey === "recipe_items" || relationKey === "packaging_items") {
      return val.map(formatRecipeLineItem);
    }
    return val.map((item) => {
      const row = item as RelationItem;
      return row?.name ?? row?.label ?? row?.bom_name ?? `#${row?.id ?? "?"}`;
    });
  }

  return val.map(String);
}

function formatMaintenanceContextEntry(
  entry?: AuditMaintenanceContext | null,
): string | null {
  if (!entry) return null;
  const parts: string[] = [];
  if (entry.equipment_status) parts.push(entry.equipment_status);
  if (entry.maintenance_date) parts.push(entry.maintenance_date);
  if (entry.requested_by) parts.push(entry.requested_by);
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function formatAuditContext(context?: AuditContext | null): string | null {
  if (!context || typeof context !== "object") return null;
  const notes: string[] = [];
  if (context.password_changed) notes.push("Password changed");
  if (context.pin_changed) notes.push("PIN changed");
  if (context.ingredient_added)
    notes.push(`Ingredient added ${context.ingredient_added.bom_name}`);
  if (context.ingredient_removed)
    notes.push(`Ingredient removed ${context.ingredient_removed.bom_name}`);
  if (context.maintenance_added) {
    const detail = formatMaintenanceContextEntry(context.maintenance_added);
    notes.push(
      detail ? `Maintenance added · ${detail}` : "Maintenance added",
    );
  }
  if (context.maintenance_updated) {
    const detail = formatMaintenanceContextEntry(context.maintenance_updated);
    notes.push(
      detail ? `Maintenance updated · ${detail}` : "Maintenance updated",
    );
  }
  if (context.maintenance_removed) {
    const detail = context.maintenance_removed.requested_by
      ? ` · ${context.maintenance_removed.requested_by}`
      : "";
    notes.push(`Maintenance removed${detail}`);
  }
  return notes.length > 0 ? notes.join(" · ") : null;
}
